import { produce } from 'immer';
import uuid from 'react-uuid';
import { create } from 'zustand';

import service from '@/services';
import { filterMap } from '@/utils/lazy';

import type { ReportColumnT, ReportItem, ReportRowT, ReportT } from '../types';

type State = {
  report: ReportT | null;
  reportBackup: ReportT | null;
  mode: DETAIL_MODE;
  dragging: boolean;
};

type Actions = {
  initReportStore: () => void;
  setState: StoreSetter<State>;
  setReport: (report: ReportT) => void;
  clearReport: () => void;
  setReportTitle: (title: string) => void;
  modifyRowHeight: (alias: string, height: number) => void;
  addRow: (rowIndex: number) => ReportRowT;
  removeRow: (alias: string) => void;
  removeColumn: (rowAlias: string, column: ReportColumnT) => void;
  addItem: (
    item: ReportItem,
    rowAlias: string,
    columnIndex?: number,
  ) => ReportColumnT;
  moveColumn: (
    currentRowAlias: string,
    currentColumn: ReportColumnT,
    targetRowAlias: string,
    targetColumnIndex: number,
  ) => void;
  updateItem: (alias: string, item: Partial<ReportItem>) => void;
  setMode: (mode: DETAIL_MODE) => void;
  setDragging: (dragging: boolean) => void;
  updateTextItem: (alias: string, text: string) => void;
  checkHasAnalysis: (chart_alias: string) => Promise<string[] | null>;
  backupReport: () => void;
  restoreReport: () => void;
  clearBackup: () => void;
};

const initialState: State = {
  report: null,
  reportBackup: null,
  mode: 'DETAIL',
  dragging: false,
};

const useReportStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setReportTitle: (title) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[setReportTitle] Report not found');
        state.report.title = title;
      }),
    );
  },
  addRow: (rowIndex: number) => {
    const newRow: ReportRowT = { alias: uuid(), columns: [] };
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[addRow] Report not found');
        state.report.rows?.splice(rowIndex, 0, newRow);
      }),
    );
    return newRow;
  },
  removeRow: (alias: string) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[removeRow] Report not found');
        state.report.rows = state.report.rows?.filter(
          (row) => row.alias !== alias,
        );
      }),
    );
  },
  addItem: (item, rowAlias, columnIndex = 0) => {
    const newColumn: ReportColumnT = {
      alias: uuid(),
      item_alias: item.alias,
    };
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[addItem] Report not found');
        const targetRow = state.report.rows?.find(
          (row) => row.alias === rowAlias,
        );
        if (!targetRow) throw new Error('[addItem] Target row not found');
        targetRow.columns.splice(columnIndex, 0, newColumn);
        state.report.items?.push(item);
      }),
    );
    return newColumn;
  },
  updateItem: (alias, newItem: Partial<ReportItem>) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[updateItem] Report not found');
        const itemToUpdate = state.report.items?.find(
          (item) => item.alias === alias,
        );
        if (itemToUpdate) {
          Object.assign(itemToUpdate, newItem);
        }
      }),
    );
  },
  removeColumn: (rowAlias: string, columnItem) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[removeColumn] Report not found');
        const targetRow = state.report.rows?.find(
          (row) => row.alias === rowAlias,
        );
        if (targetRow) {
          targetRow.columns = targetRow.columns?.filter(
            (column) => column.item_alias !== columnItem.item_alias,
          );
        }

        state.report.items = state.report.items?.filter(
          (item) => item.alias !== columnItem.item_alias,
        );

        state.report.rows = state.report.rows?.filter(
          (row) => row.columns.length > 0,
        );
      }),
    );
  },
  moveColumn: (
    currentRowAlias: string,
    currentColumn: ReportColumnT,
    targetRowAlias: string,
    targetColumnIndex: number,
  ) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[moveColumn] Report not found');

        const currColumn = currentColumn; // Immer handles immutability, no need for deep copy here

        // Handle moving within the same row
        if (currentRowAlias === targetRowAlias) {
          const targetRow = state.report.rows?.find(
            (row) => row.alias === currentRowAlias,
          );
          if (!targetRow) return;

          const fromIndex = targetRow.columns.findIndex(
            (c) => c.alias === currColumn.alias,
          );
          if (fromIndex === -1) return;

          const [movedItem] = targetRow.columns.splice(fromIndex, 1);
          targetRow.columns.splice(targetColumnIndex, 0, movedItem);
        } else {
          // Handle moving to a different row
          const currentRow = state.report.rows?.find(
            (row) => row.alias === currentRowAlias,
          );
          if (!currentRow)
            throw new Error('[moveColumn] Current row not found');

          const targetRow = state.report.rows?.find(
            (row) => row.alias === targetRowAlias,
          );
          if (!targetRow) throw new Error('[moveColumn] Target row not found');

          const currentColumnIndex = currentRow.columns.findIndex(
            (column) => column.alias === currColumn.alias,
          );

          const [movedColumn] = currentRow.columns.splice(
            currentColumnIndex,
            1,
          );
          targetRow.columns.splice(targetColumnIndex, 0, movedColumn);

          state.report.rows = state.report.rows?.filter(
            (row) => row.columns.length > 0,
          );
        }
      }),
    );
  },
  modifyRowHeight: (alias: string, height: number) => {
    set(
      produce((state: State) => {
        if (!state.report)
          throw new Error('[modifyRowHeight] Report not found');
        const targetRow = state.report.rows?.find((row) => row.alias === alias);
        if (targetRow) {
          targetRow.height = height;
        }
      }),
    );
  },
  setMode: (mode) => set({ mode }),
  clearReport: () => {
    set({ report: null, reportBackup: null, dragging: false, mode: 'DETAIL' });
  },
  setReport: (report) => {
    set({ report });
  },
  setDragging: (dragging) => set({ dragging }),
  updateTextItem: (alias, text) => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[updateTextItem] Report not found');
        const itemToUpdate = state.report.items?.find(
          (item) => item.alias === alias,
        );
        if (itemToUpdate) {
          itemToUpdate.text = text;
        }
      }),
    );
  },
  checkHasAnalysis: async (chart_alias: string) => {
    const report = get().report;
    if (!report) return null;
    if (!report.items) return null;
    const hasAnalysis = await service.chart.checkHasAnalysis(chart_alias);
    if (!hasAnalysis.has_analysis) return null;

    return filterMap(report.items, (item) => {
      if (item.analysis?.reference_id === chart_alias) {
        return item.alias;
      }
      return null;
    });
  },
  backupReport: () => {
    set(
      produce((state: State) => {
        if (!state.report) throw new Error('[backupReport] Report not found');
        state.reportBackup = JSON.parse(JSON.stringify(state.report)); // Deep copy
      }),
    );
  },
  restoreReport: () => {
    set(
      produce((state: State) => {
        if (!state.reportBackup)
          throw new Error('[restoreReport] Backup not found');
        state.report = state.reportBackup;
        state.reportBackup = null;
      }),
    );
  },
  clearBackup: () => {
    set(
      produce((state: State) => {
        state.reportBackup = null;
      }),
    );
  },
  setState: set,
  initReportStore: () => set(initialState),
}));

export default useReportStore;
